import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, X, Plus } from "lucide-react";

const LEGAL_BASIS = {
  consent: "Consentiment (art. 6.1.a)",
  contract: "Execució d'un contracte (art. 6.1.b)",
  legal_obligation: "Obligació legal (art. 6.1.c)",
  vital_interest: "Interès vital (art. 6.1.d)",
  public_task: "Tasca d'interès públic (art. 6.1.e)",
  legitimate_interest: "Interès legítim (art. 6.1.f)",
};

const DATA_CATEGORY_SUGGESTIONS = [
  "Nom i cognoms", "DNI/NIF", "Email", "Telèfon", "Adreça postal",
  "Dades bancàries", "Imatge (CCTV)", "Dades de salut", "Nòmines",
  "Historial de compres", "Preferències alimentàries", "Dades de menors",
];

const SUBJECT_SUGGESTIONS = ["Clients", "Treballadors", "Proveïdors", "Candidats a feina", "Menors"];

const BLANK = {
  name: "", purpose: "", legal_basis: "contract", data_categories: [],
  data_subjects: [], controller_name: "", controller_email: "",
  processor_name: "", retention_period: "", international_transfer: false,
  transfer_country: "", security_measures: "", status: "active", notes: "",
};

export default function ProcessingActivityForm({ open, onClose, item, venueId, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [catInput, setCatInput] = useState("");
  const [subjectInput, setSubjectInput] = useState("");

  useEffect(() => {
    setForm(item ? { ...BLANK, ...item } : BLANK);
  }, [item, open]);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const addTag = (field, value, setInput) => {
    const v = value.trim();
    if (!v) return;
    if (!form[field].includes(v)) set(field, [...form[field], v]);
    setInput("");
  };

  const removeTag = (field, value) => set(field, form[field].filter(x => x !== value));

  const handleSave = async () => {
    if (!form.name || !form.purpose) return;
    setSaving(true);
    if (item?.id) {
      await hosys.entities.DataProcessingActivity.update(item.id, { ...form, venue_id: venueId });
    } else {
      await hosys.entities.DataProcessingActivity.create({ ...form, venue_id: venueId });
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Editar activitat" : "Nova activitat de tractament"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label>Nom de l'activitat *</Label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex: Gestió de reserves de clients" />
          </div>
          <div>
            <Label>Finalitat del tractament *</Label>
            <Textarea value={form.purpose} onChange={e => set("purpose", e.target.value)} rows={2} placeholder="Per a quina finalitat es tracten les dades..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Base legal</Label>
              <Select value={form.legal_basis} onValueChange={v => set("legal_basis", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(LEGAL_BASIS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Període de conservació</Label>
              <Input value={form.retention_period} onChange={e => set("retention_period", e.target.value)} placeholder="Ex: 5 anys, 3 anys..." />
            </div>
          </div>

          {/* Data categories */}
          <div>
            <Label>Categories de dades</Label>
            <div className="flex flex-wrap gap-1.5 mb-1.5 mt-1">
              {form.data_categories.map(c => (
                <Badge key={c} className="bg-blue-100 text-blue-700 border-0 gap-1 pr-1">
                  {c}
                  <button onClick={() => removeTag("data_categories", c)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={catInput} onChange={e => setCatInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag("data_categories", catInput, setCatInput); }}}
                placeholder="Afegir categoria i prémer Enter..." className="text-sm" />
              <Button type="button" variant="outline" size="sm" onClick={() => addTag("data_categories", catInput, setCatInput)}><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {DATA_CATEGORY_SUGGESTIONS.filter(s => !form.data_categories.includes(s)).map(s => (
                <button key={s} onClick={() => set("data_categories", [...form.data_categories, s])}
                  className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 hover:bg-blue-100 hover:text-blue-600 transition-colors">
                  + {s}
                </button>
              ))}
            </div>
          </div>

          {/* Data subjects */}
          <div>
            <Label>Categories d'interessats</Label>
            <div className="flex flex-wrap gap-1.5 mb-1.5 mt-1">
              {form.data_subjects.map(s => (
                <Badge key={s} className="bg-purple-100 text-purple-700 border-0 gap-1 pr-1">
                  {s}
                  <button onClick={() => removeTag("data_subjects", s)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {SUBJECT_SUGGESTIONS.filter(s => !form.data_subjects.includes(s)).map(s => (
                <button key={s} onClick={() => set("data_subjects", [...form.data_subjects, s])}
                  className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 hover:bg-purple-100 hover:text-purple-600 transition-colors">
                  + {s}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Responsable del tractament</Label>
              <Input value={form.controller_name} onChange={e => set("controller_name", e.target.value)} placeholder="Nom del responsable" />
            </div>
            <div>
              <Label>Encarregat (si n'hi ha)</Label>
              <Input value={form.processor_name} onChange={e => set("processor_name", e.target.value)} placeholder="Ex: Gestoría, CRM extern..." />
            </div>
          </div>
          <div>
            <Label>Mesures de seguretat</Label>
            <Textarea value={form.security_measures} onChange={e => set("security_measures", e.target.value)} rows={2} placeholder="Xifrat, control d'accés, còpies de seguretat..." />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} placeholder="Observacions addicionals..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={!form.name || !form.purpose || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {item ? "Actualitzar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}